// The console sign-on screen in the live look: sign in or a new operator account on the left, the network's status
// (the public health endpoint) on the right, over the game's footage (Footage). The caller does the signing in; this
// screen only collects and shows. The other ways to sign in (Hep.gg, Steam) sit in one row under the operator sign-in,
// each shown only when the server's auth config offers it.

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { BoltMark } from './BoltMark';

/** The footage files (apps/beta/dev/footage; assets/footage, served at /footage/) */
const FOOTAGE = `${import.meta.env.BASE_URL}footage/`;

/**
 * The game's footage full screen behind the sign-on: the poster at once, the looping video only after the screen has
 * painted (so it never delays it) and faded in once it plays; with reduced motion asked for, the poster alone. A dark
 * vignette keeps the panel readable over it.
 */
function Footage() {
  const [video, setVideo] = useState(false);
  const [playing, setPlaying] = useState(false);
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    let raf = requestAnimationFrame(() => (raf = requestAnimationFrame(() => setVideo(true))));
    return () => cancelAnimationFrame(raf);
  }, []);
  useEffect(() => {
    // autoplay can be refused (power saving, a browser setting): the poster stays
    if (video) void ref.current?.play().catch(() => undefined);
  }, [video]);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden bg-void" aria-hidden>
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${FOOTAGE}poster.jpg)` }} />
      {video && (
        <video
          ref={ref}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${playing ? 'opacity-100' : 'opacity-0'}`}
          muted
          loop
          playsInline
          autoPlay
          preload="auto"
          poster={`${FOOTAGE}poster.jpg`}
          onPlaying={() => setPlaying(true)}
        >
          <source src={`${FOOTAGE}loop.webm`} type='video/webm; codecs="vp9"' />
          <source src={`${FOOTAGE}loop.mp4`} type="video/mp4" />
        </video>
      )}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 75% at 50% 50%, rgba(7,9,12,0.55) 0%, rgba(7,9,12,0.25) 55%, rgba(7,9,12,0.55) 100%)' }} />
    </div>
  );
}

interface Health {
  ok: boolean;
  online: number;
  content: string;
  client: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
const clock = (ms: number) => {
  const d = new Date(ms);
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
};

/** The network status column (polled every 10 s). */
function NetworkStatus({ build }: { build: string }) {
  const [h, setH] = useState<Health | null>(null);
  const [down, setDown] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const poll = async () => {
      try {
        const r = await fetch('/api/health');
        setH((await r.json()) as Health);
        setDown(!r.ok);
      } catch {
        setDown(true);
      }
    };
    void poll();
    const a = setInterval(() => void poll(), 10_000);
    const b = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(a);
      clearInterval(b);
    };
  }, []);
  const row = (k: string, v: ReactNode, tone?: string) => (
    <div className="flex h-[24px] items-center justify-between border-b border-line-subtle text-[12px]">
      <span className="text-t3">{k}</span>
      <span className={`num ${tone ?? 'text-t1'}`}>{v}</span>
    </div>
  );
  return (
    <div className="flex flex-col">
      <div className="lbl mb-1">Network status</div>
      {row(
        'Gateway',
        <span className="flex items-center gap-1.5">
          <span className={`inline-block h-[8px] w-[8px] ${down ? 'blink bg-p1' : h ? 'bg-t2' : 'bg-t4'}`} />
          {down ? 'UNREACHABLE' : h ? 'IN SERVICE' : 'CHECKING'}
        </span>,
        down ? 'text-p1' : 'text-t1',
      )}
      {row('Operators online', h ? h.online : '-')}
      {row('Content', h ? h.content : '-')}
      {row('Client build', h ? h.client : build)}
      {row('Server time', `${clock(now)}Z`)}
      <div className="mt-3 text-[11px] leading-snug text-t4">Authorized operators only. Sessions and privileged actions are logged. One live console per operator: signing in here closes your other sessions.</div>
    </div>
  );
}

/** The ways to sign in besides the operator ID the server offers (its auth config). */
export interface SignOnMethods {
  sso: boolean;
  steam?: boolean;
}

export function SignOn(props: { build: string; methods: SignOnMethods; detail?: string | null; onSubmit: (mode: 'login' | 'register', user: string, pass: string) => Promise<void>; onSso: () => void; onSteam?: () => Promise<void> }) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [error, setError] = useState<string | null>(props.detail ?? null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (props.detail) setError(props.detail);
  }, [props.detail]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await props.onSubmit(mode, user, pass);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };
  // the other ways in, side by side under the operator sign-in, each only when the server offers it
  const alternates: ReactNode[] = [];
  if (props.methods.sso)
    alternates.push(
      <a key="sso" className="btn h-8" href="/api/auth/sso" onClick={props.onSso}>
        Continue with Hep.gg
      </a>,
    );
  if (props.methods.steam && props.onSteam) {
    const onSteam = props.onSteam;
    alternates.push(
      <button
        key="steam"
        type="button"
        className="btn h-8"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            await onSteam();
          } catch (err) {
            setError(err instanceof Error ? err.message : mode === 'register' ? 'Steam sign-up failed. Try again.' : 'Steam sign-in failed. Try again.');
          } finally {
            setBusy(false);
          }
        }}
      >
        {/* the same call either way: Steam's sign-in makes the account when the Steam account has none */}
        {mode === 'register' ? 'Sign up with Steam' : 'Sign in with Steam'}
      </button>,
    );
  }
  return (
    <div className="pointer-events-auto fixed inset-0 z-[90] flex items-center justify-center bg-void">
      <Footage />
      <div className="panel relative grid w-[760px] max-w-[calc(100vw-24px)] grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] shadow-[0_18px_60px_rgba(0,0,0,0.6)]">
        <div className="panel-head col-span-2">
          <BoltMark size={15} />
          <span>Console Sign-On</span>
          <span className="num ml-auto font-normal text-t3">GC-OPS {props.build}</span>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4 border-r border-line-subtle p-5">
          <div>
            <div className="text-[22px] font-semibold tracking-[0.06em] text-t1">GRIDCONTROLLER</div>
            <div className="text-[12.5px] text-t3">Maplewood Valley operations network</div>
          </div>
          <div className="seg w-full">
            <button type="button" className={`btn btn-sm flex-1 ${mode === 'login' ? 'on' : ''}`} onClick={() => setMode('login')}>
              Sign in
            </button>
            <button type="button" className={`btn btn-sm flex-1 ${mode === 'register' ? 'on' : ''}`} onClick={() => setMode('register')}>
              New operator
            </button>
          </div>
          {mode === 'register' && (
            <div className="-mt-1 text-[12px] leading-snug text-t2">You start with an exercise-bike generator in the garage and a street that keeps losing power. Grow into rooftop solar, land and power plants, then run the town's electric utility.</div>
          )}
          <label className="flex flex-col gap-1">
            <span className="lbl">Operator ID</span>
            <input className="field" value={user} onChange={(e) => setUser(e.target.value)} autoComplete="username" autoFocus maxLength={20} />
            {mode === 'register' && <span className="text-[11px] text-t4">3 to 20 letters, numbers or underscores. Your company is named after it; you can rename it later.</span>}
          </label>
          <label className="flex flex-col gap-1">
            <span className="lbl">Passphrase</span>
            <input className="field" type="password" value={pass} onChange={(e) => setPass(e.target.value)} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} />
            {mode === 'register' && <span className="text-[11px] text-t4">At least 8 characters.</span>}
          </label>
          {error && <div className="border-l-2 border-p1 bg-s2 px-2 py-1 text-[12px] text-p1">{error}</div>}
          <button className="btn btn-primary h-8" disabled={busy || !user || !pass}>
            {busy ? 'Authenticating...' : mode === 'login' ? 'Sign in' : 'Create operator account'}
          </button>
          {alternates.length > 0 && (
            <>
              <div className="flex items-center gap-2 text-[11px] text-t4">
                <div className="h-px flex-1 bg-line" />
                OR
                <div className="h-px flex-1 bg-line" />
              </div>
              <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${alternates.length}, minmax(0, 1fr))` }}>
                {alternates}
              </div>
            </>
          )}
        </form>
        <div className="bg-s1 p-5">
          <NetworkStatus build={props.build} />
        </div>
      </div>
    </div>
  );
}
